import {
  createEmptyOpeningHours,
  normalizeOpeningHours,
  serializeOpeningHours,
} from '../constants/openingHours';
export const emptyPlaceForm = {
  placeId: '',
  isVerified: false,
  loadTrackingEnabled: true,
  autoVisitDetectionEnabled: false,
  autoVisitGeofenceRadiusMeters: 120,
  status: 'pending',
  behaviorType: '',
  placeKind: 'standalone',
  floorsText: '',
  availableFloors: [],
  containment: null,
  source: 'system',
  name: {
    he: '',
    en: '',
    es: '',
  },
  shortName: {
    he: '',
    en: '',
    es: '',
  },
  branchName: {
    he: '',
    en: '',
    es: '',
  },
  city: {
    he: '',
    en: '',
    es: '',
  },
  country: {
    he: '',
    en: '',
    es: '',
  },
  address: {
    he: '',
    en: '',
    es: '',
  },
  categories: [],
  lng: '',
  lat: '',
  footprintPoints: [],
  frontagePoints: [],
  footprintParts: [],
  footprintHeightLevel: 1,
  footprintMetadata: null,
  openingHours: createEmptyOpeningHours(),
};

const getRingPoints = (ring) => {
  if (!Array.isArray(ring) || ring.length < 4) return [];

  const points = ring.map((point) => [Number(point?.[0]), Number(point?.[1])]);
  const first = points[0];
  const last = points[points.length - 1];
  const isClosed = first?.[0] === last?.[0] && first?.[1] === last?.[1];
  return (isClosed ? points.slice(0, -1) : points).filter(
    (point) => Number.isFinite(point[0]) && Number.isFinite(point[1]),
  );
};

export const getFootprintParts = (footprint) => {
  const polygons =
    footprint?.type === 'Polygon'
      ? [footprint.coordinates]
      : footprint?.type === 'MultiPolygon'
        ? footprint.coordinates
        : [];
  return polygons
    .map((polygon) => getRingPoints(polygon?.[0]))
    .filter((points) => points.length >= 3);
};

export const toPlaceForm = (place) => {
  return {
    ...emptyPlaceForm,
    placeId: place.placeId || place.id || '',
    isVerified: Boolean(place.isVerified),
    loadTrackingEnabled: place.loadTrackingEnabled !== false,
    autoVisitDetectionEnabled: place.autoVisitDetectionEnabled === true,
    autoVisitGeofenceRadiusMeters: Number(place.autoVisitGeofenceRadiusMeters) || 120,
    status: place.status || 'pending',
    behaviorType: place.behaviorType || '',
    placeKind: place.placeKind || 'standalone',
    floorsText: Array.isArray(place.floors) ? place.floors.join('\n') : '',
    availableFloors: Array.isArray(place.availableFloors) ? place.availableFloors : [],
    containment: place.containment || null,
    source: place.source || 'system',
    name: {
      he: place.name?.he || '',
      en: place.name?.en || '',
      es: place.name?.es || '',
    },
    shortName: {
      he: place.shortName?.he || '',
      en: place.shortName?.en || '',
      es: place.shortName?.es || '',
    },
    branchName: {
      he: place.branchName?.he || '',
      en: place.branchName?.en || '',
      es: place.branchName?.es || '',
    },
    city: {
      he: place.city?.he || '',
      en: place.city?.en || '',
      es: place.city?.es || '',
    },
    country: {
      he: place.country?.he || '',
      en: place.country?.en || '',
      es: place.country?.es || '',
    },
    address: {
      he: place.address?.he || '',
      en: place.address?.en || '',
      es: place.address?.es || '',
    },
    categories: Array.isArray(place.categories) ? place.categories : [],
    lng: place.location?.coordinates?.[0] ?? '',
    lat: place.location?.coordinates?.[1] ?? '',
    footprintPoints: getFootprintParts(place.footprint)[0] || [],
    frontagePoints:
      place.frontage?.type === 'LineString' && Array.isArray(place.frontage.coordinates)
        ? place.frontage.coordinates
            .slice(0, 2)
            .map((point) => [Number(point[0]), Number(point[1])])
        : [],
    footprintParts: getFootprintParts(place.footprint),
    footprintHeightLevel:
      Number.isInteger(place.footprintHeightLevel) &&
      place.footprintHeightLevel >= 0 &&
      place.footprintHeightLevel <= 3
        ? place.footprintHeightLevel
        : 1,
    footprintMetadata: place.footprintMetadata || null,
    openingHours: normalizeOpeningHours(place.openingHours || []),
  };
};
export const toFootprintPayload = (placeForm) => {
  const footprintParts = (
    placeForm.footprintParts?.length ? placeForm.footprintParts : [placeForm.footprintPoints || []]
  ).filter((points) => points.length >= 3);
  const closedParts = footprintParts.map((points) => [[...points, points[0]]]);
  const footprint =
    closedParts.length === 0
      ? null
      : closedParts.length === 1
        ? { type: 'Polygon', coordinates: closedParts[0] }
        : { type: 'MultiPolygon', coordinates: closedParts };

  return {
    footprint,
    frontage:
      placeForm.frontagePoints?.length === 2
        ? { type: 'LineString', coordinates: placeForm.frontagePoints.map((point) => [...point]) }
        : null,
    footprintHeightLevel: Math.min(3, Math.max(0, Number(placeForm.footprintHeightLevel) || 0)),
  };
};
export const toPlaceMapPayload = (placeForm) => {
  const lng = placeForm.lng === '' ? null : Number(placeForm.lng);
  const lat = placeForm.lat === '' ? null : Number(placeForm.lat);

  return {
    ...toFootprintPayload(placeForm),
    location:
      lng === null || lat === null
        ? { coordinates: [] }
        : { type: 'Point', coordinates: [lng, lat] },
  };
};
export const toPlacePayload = (placeForm) => {
  const lng = placeForm.lng === '' ? null : Number(placeForm.lng);
  const lat = placeForm.lat === '' ? null : Number(placeForm.lat);
  const footprintParts = (
    placeForm.footprintParts?.length ? placeForm.footprintParts : [placeForm.footprintPoints || []]
  ).filter((points) => points.length >= 3);
  const closedParts = footprintParts.map((points) => [[...points, points[0]]]);
  const footprint =
    closedParts.length === 0
      ? null
      : closedParts.length === 1
        ? { type: 'Polygon', coordinates: closedParts[0] }
        : { type: 'MultiPolygon', coordinates: closedParts };

  return {
    isVerified: placeForm.isVerified,
    loadTrackingEnabled: placeForm.loadTrackingEnabled,
    autoVisitDetectionEnabled: placeForm.loadTrackingEnabled && placeForm.autoVisitDetectionEnabled,
    autoVisitGeofenceRadiusMeters: Math.min(
      1000,
      Math.max(100, Number(placeForm.autoVisitGeofenceRadiusMeters) || 120),
    ),
    status: placeForm.status,
    behaviorType: placeForm.behaviorType || undefined,
    placeKind: placeForm.placeKind,
    floors:
      placeForm.placeKind === 'container'
        ? String(placeForm.floorsText || '')
            .split(/\r?\n/)
            .map((floor) => floor.trim())
            .filter(Boolean)
        : [],
    containment: placeForm.containment,
    source: placeForm.source,
    name: placeForm.name,
    shortName: placeForm.shortName,
    branchName: placeForm.branchName,
    city: placeForm.city,
    country: placeForm.country,
    address: placeForm.address,
    categories: Array.isArray(placeForm.categories) ? placeForm.categories : [],
    openingHours: serializeOpeningHours(placeForm.openingHours),
    footprint,
    frontage:
      placeForm.frontagePoints?.length === 2
        ? { type: 'LineString', coordinates: placeForm.frontagePoints.map((point) => [...point]) }
        : null,
    footprintHeightLevel: Math.min(3, Math.max(0, Number(placeForm.footprintHeightLevel) || 0)),
    location:
      lng === null || lat === null
        ? {
            coordinates: [],
          }
        : {
            type: 'Point',
            coordinates: [lng, lat],
          },
  };
};
